import { Controller } from "@hotwired/stimulus"

// Renderuje wykres Chart.js na <canvas>. Dane i opcje przychodzą z serwera przez Values API
// (Tailwind::ChartComponent), dzięki czemu w szablonie nie ma ani linijki JS-a.
export default class extends Controller {
  // Kolory malowane na canvasie idą przez Values, nie przez stałe modułowe — kontroler jest
  // współdzielony przez raporty, więc każdy z nich może nadpisać odcień z HTML-a. Chart.js
  // nie przyjmuje klas Tailwinda, więc podajemy wartości CSS (`static classes` tu nie zadziała).
  //
  // `textColor` (gray-800) obsługuje tytuł, podtytuł i etykiety osi — to jeden odcień, więc
  // jedna wartość. Składnia z przecinkami: bezprzecinkowa to CSS Color 4 (Safari od 16.4),
  // a projekt nie ma browserslist, więc na starszych silnikach cicho by nie zadziałała.
  // `legendGray` (gray-500) to surowa trójka rgb — tak samo jak `hatch` w datasetach,
  // bo wchodzi do `rgba(...)` i do kreskowania z własną nieprzezroczystością.
  static values = {
    type: String,
    labels: Array,
    datasets: Array,
    title: String,
    subtitle: String,
    currency: String,
    stacked: Boolean,
    dedupeLegend: Boolean,
    legendTypeLabels: Object,
    maintainAspectRatio: { type: Boolean, default: true },
    borderRadius: Number,
    textColor: { type: String, default: "rgb(31, 41, 55)" },
    legendGray: { type: String, default: "107, 114, 128" },
    // Zapas nad najwyższym słupkiem, żeby wartość nie kleiła się do górnej krawędzi wykresu.
    suggestedMaxMargin: { type: Number, default: 10 }
  }

  // Chart.js siedzi w osobnym packu (fakturownia-7-charts) dołączanym w <head> tylko na stronach
  // z wykresami. Przy nawigacji Turbo pack trafia tam asynchronicznie i Turbo nie czeka na skrypty
  // (czeka tylko na arkusze stylów), więc `connect()` regularnie wyprzedza `window.Chart`.
  // Bez nasłuchu na gotowość packa wykres nie narysowałby się do pełnego przeładowania strony.
  connect() {
    if (window.Chart) {
      this.drawChart()
      return
    }

    this.drawWhenChartJsReady = () => this.drawChart()
    document.addEventListener("chartjs:ready", this.drawWhenChartJsReady, { once: true })
  }

  disconnect() {
    if (this.drawWhenChartJsReady) {
      document.removeEventListener("chartjs:ready", this.drawWhenChartJsReady)
      this.drawWhenChartJsReady = null
    }
    if (!this.chart) return

    this.chart.destroy()
    this.chart = null
    this.hatchedLegendSwatch = null
  }

  drawChart() {
    // NIE `this.context` — to pole trzyma Stimulus (czyta z niego `scope` i `data`).
    this.canvasContext = this.element.getContext("2d")
    this.chart = new window.Chart(this.canvasContext, {
      type: this.typeValue,
      data: { labels: this.labelsValue, datasets: this.chartDatasets() },
      options: this.chartOptions()
    })
  }

  // Datasety z polem `hatch` ("r, g, b") dostają wypełnienie kreskowaniem w kolorze kraju.
  // CanvasPattern nie przechodzi przez JSON, więc serwer przysyła sam kolor.
  chartDatasets() {
    return this.datasetsValue.map((dataset) => {
      if (!dataset.hatch) return dataset

      return { ...dataset, backgroundColor: this.hatchPattern(dataset.hatch) }
    })
  }

  // Kafel 8×8: jasne tło + ukośne kreski w tym samym kolorze. Segmenty w rogach sprawiają,
  // że wzór powtarza się bezszwowo. Kreska odróżnia wydatki od przychodów niezależnie
  // od koloru (druk, dostępność).
  hatchPattern(rgb) {
    const tile = document.createElement("canvas")
    tile.width = 8
    tile.height = 8

    const tileContext = tile.getContext("2d")
    tileContext.fillStyle = `rgba(${rgb}, 0.15)`
    tileContext.fillRect(0, 0, 8, 8)
    tileContext.strokeStyle = `rgba(${rgb}, 0.9)`
    tileContext.lineWidth = 1.5
    tileContext.beginPath()
    tileContext.moveTo(0, 8)
    tileContext.lineTo(8, 0)
    tileContext.moveTo(-2, 2)
    tileContext.lineTo(2, -2)
    tileContext.moveTo(6, 10)
    tileContext.lineTo(10, 6)
    tileContext.stroke()

    return this.canvasContext.createPattern(tile, "repeat")
  }

  chartOptions() {
    return {
      maintainAspectRatio: this.maintainAspectRatioValue,
      borderRadius: this.borderRadiusValue,
      scales: {
        x: { stacked: this.stackedValue, ticks: { color: this.textColorValue } },
        y: {
          stacked: this.stackedValue,
          suggestedMax: this.suggestedMax(),
          ticks: { color: this.textColorValue, callback: (value) => this.formatValue(value) }
        }
      },
      plugins: this.chartPlugins()
    }
  }

  // Bez datasetów (zakres dat bez faktur) nie narzucamy nic — Chart.js sam dobierze oś.
  suggestedMax() {
    const values = this.datasetsValue.flatMap((dataset) => dataset.data)
    if (values.length === 0) return undefined

    return Math.max(...values) + this.suggestedMaxMarginValue
  }

  formatValue(value) {
    const formatted = value.toLocaleString()

    return this.hasCurrencyValue ? `${formatted} ${this.currencyValue}` : formatted
  }

  chartPlugins() {
    const plugins = {
      tooltip: { callbacks: { label: (context) => this.tooltipLabel(context) } }
    }

    if (this.dedupeLegendValue) {
      plugins.legend = {
        labels: { generateLabels: (chart) => this.legendLabels(chart) },
        onClick: (event, legendItem, legend) => this.toggleLabelDatasets(legendItem, legend)
      }
    }
    if (this.hasTitleValue) {
      plugins.title = {
        display: true,
        text: this.titleValue,
        color: this.textColorValue,
        font: { size: 18, weight: 600, family: "Inter" }
      }
    }
    if (this.hasSubtitleValue) {
      plugins.subtitle = { display: true, text: this.subtitleValue, color: this.textColorValue, font: { size: 12 } }
    }

    return plugins
  }

  tooltipLabel(context) {
    let label = context.dataset.label || ""

    if (label) label += ": "
    if (context.parsed.y !== null) label += context.parsed.y

    return this.hasCurrencyValue ? `${label} ${this.currencyValue}` : label
  }

  // Każda etykieta raz (kolor z datasetu przychodów), a na końcu dwa wpisy objaśniające
  // typ słupka: pełny = przychody, kreskowany = wydatki.
  legendLabels(chart) {
    const seen = new Set()
    const items = []

    chart.data.datasets.forEach((dataset, index) => {
      if (seen.has(dataset.label)) return

      seen.add(dataset.label)
      items.push(this.legendItem({
        text: dataset.label,
        fillStyle: dataset.backgroundColor,
        hidden: !chart.isDatasetVisible(index),
        datasetIndex: index
      }))
    })

    if (this.hasLegendTypeLabelsValue) {
      items.push(this.legendItem({
        text: this.legendTypeLabelsValue.solid,
        fillStyle: `rgba(${this.legendGrayValue}, 0.6)`,
        datasetIndex: -1
      }))
      items.push(this.legendItem({
        text: this.legendTypeLabelsValue.hatched,
        fillStyle: this.hatchedSwatch,
        datasetIndex: -1
      }))
    }

    return items
  }

  legendItem(attributes) {
    return { strokeStyle: "rgba(0, 0, 0, 0.1)", lineWidth: 1, ...attributes }
  }

  get hatchedSwatch() {
    if (!this.hatchedLegendSwatch) this.hatchedLegendSwatch = this.hatchPattern(this.legendGrayValue)

    return this.hatchedLegendSwatch
  }

  // Kliknięcie kraju przełącza oba jego datasety (przychód + wydatek) naraz. Wpisy objaśniające
  // typ słupka (datasetIndex -1) nie odpowiadają żadnej serii, więc niczego nie przełączają.
  toggleLabelDatasets(legendItem, legend) {
    if (legendItem.datasetIndex === -1) return

    const chart = legend.chart
    const indices = []

    chart.data.datasets.forEach((dataset, index) => {
      if (dataset.label === legendItem.text) indices.push(index)
    })

    const anyVisible = indices.some((index) => chart.isDatasetVisible(index))
    indices.forEach((index) => (anyVisible ? chart.hide(index) : chart.show(index)))
  }
}
